import { styled } from 'baseui'
import type { Theme } from 'baseui'
import type { StyleObject } from 'styletron-standard'
import { pulseLighting } from '@/shared/theme/pulseLighting'
import { RAIL_EASE, RAIL_SPRING, railStagger, railStaggerDelay } from '@/shared/theme/railSlot'
import type { PillBox } from '@/shared/components/SDComponentsNewDesign/SDButtonGroup/useSlidingPill'
import selectraPrimitives from '@/shared/theme/themePrimitives'
import type { SaleProductStatus } from './saleProductStatus'
import { enter, popIn, riseIn } from './motion'

const radius = (value: string): StyleObject => ({
  borderTopLeftRadius: value,
  borderTopRightRadius: value,
  borderBottomLeftRadius: value,
  borderBottomRightRadius: value,
})

const border = (color: string): StyleObject => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

export const SaleGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'minmax(13.75rem, 1fr) minmax(0, 2.8fr) minmax(15rem, 1.05fr)',
  gridTemplateRows: 'minmax(0, 1fr)',
  flex: '1 1 auto',
  minHeight: 0,
  gap: '0.625rem',
  alignItems: 'stretch',
  width: '100%',
  minWidth: 0,
})

export const SaleCard = styled('section', ({ $theme }) => ({
  ...border($theme.colors.borderPulseSaleCard),
  ...radius('0.75rem'),
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  paddingTop: '1.25rem',
  paddingBottom: '1.25rem',
  paddingLeft: '1.375rem',
  paddingRight: '1.375rem',
  backgroundColor: $theme.colors.white,
  boxSizing: 'border-box',
}))

const SALE_TITLE_FONT = selectraPrimitives.pulseSaleTitleFontFamily
const SALE_LIST_FONT = selectraPrimitives.pulseSaleListFontFamily

export const SideTitle = styled('h2', ({ $theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  fontFamily: SALE_TITLE_FONT,
  fontSize: '0.9375rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  color: $theme.colors.textPulseSaleStrong,
}))

export const ProductList = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  minWidth: 0,
  marginTop: 0,
  marginBottom: 0,
  paddingLeft: 0,
  listStyle: 'none',
})

const statusAccent = ($theme: Theme, status: SaleProductStatus): string => {
  if (status === 'validated') return $theme.colors.iconPulseSaleOk
  if (status === 'waiting') return $theme.colors.iconPulseSaleWait
  if (status === 'failed') return $theme.colors.iconPulseSaleError

  return $theme.colors.primaryPulse
}

const selectedBackground = ($theme: Theme, status: SaleProductStatus): string => {
  if (status === 'validated') return $theme.colors.bgPulseSaleOkSelected
  if (status === 'waiting') return $theme.colors.bgPulseSaleWaitSelected
  if (status === 'failed') return $theme.colors.bgPulseSaleErrorSelected

  return $theme.colors.bgPulseField
}

const selectedText = ($theme: Theme, status: SaleProductStatus): string => {
  if (status === 'validated') return $theme.colors.textPulseSaleOkSelected
  if (status === 'waiting') return $theme.colors.textPulseSaleWaitSelected
  if (status === 'failed') return $theme.colors.textPulseSaleErrorSelected

  return $theme.colors.primaryPulse
}

const productItemColors = ($theme: Theme, status: SaleProductStatus, selected: boolean): StyleObject =>
  selected
    ? {
        backgroundColor: selectedBackground($theme, status),
        color: selectedText($theme, status),
        fontWeight: 600,
      }
    : { backgroundColor: 'transparent', color: $theme.colors.textPulseSaleLabel }

export const ProductItem = styled<'button', { $status: SaleProductStatus; $selected: boolean }>(
  'button',
  ({ $theme, $status, $selected }) => ({
    ...radius('0.5rem'),
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    width: '100%',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '0.625rem',
    paddingRight: '0.625rem',
    borderTopWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    fontFamily: SALE_LIST_FONT,
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 400,
    textAlign: 'left',
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: '150ms',
    ...productItemColors($theme, $status, $selected),
    ...($selected
      ? enter(
          { '0%': { transform: 'scale(0.97)' }, '60%': { transform: 'scale(1.01)' }, '100%': { transform: 'none' } },
          280,
        )
      : {}),
    ':hover': $selected
      ? {}
      : { backgroundColor: $theme.colors.bgPulseTrack, color: $theme.colors.textPulseSaleStrong },
    ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseFocus },
  }),
)

const productIconColors = ($theme: Theme, status: SaleProductStatus, selected: boolean): StyleObject => {
  if (status !== 'idle') return { color: statusAccent($theme, status) }

  return { color: selected ? 'inherit' : $theme.colors.textPulseSaleMuted }
}

export const ProductIcon = styled<'span', { $status: SaleProductStatus; $selected: boolean }>(
  'span',
  ({ $theme, $status, $selected }) => ({
    ...radius('0.375rem'),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '1.5rem',
    height: '1.5rem',
    marginTop: '-2px',
    marginBottom: '-2px',
    ...productIconColors($theme, $status, $selected),
    ...($selected && $status === 'idle'
      ? enter({ from: { transform: 'scale(0.6) rotate(-14deg)' }, to: { transform: 'none' } }, 420, '40ms', RAIL_SPRING)
      : {}),
  }),
)

export const PanelBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  minWidth: 0,
})

export const PanelGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  minWidth: 0,
})

export const productSearchOverrides = {
  ControlContainer: { style: { marginBottom: '0.5rem' } },
}

export const SectionHeader = styled('button', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '100%',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  backgroundColor: 'transparent',
  color: $theme.colors.textPulseSaleMuted,
  fontFamily: SALE_TITLE_FONT,
  textAlign: 'left',
  cursor: 'pointer',
  ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseFocus },
}))

export const CountPill = styled('span', ({ $theme }) => ({
  ...radius('62.4375rem'),
  minWidth: '1.25rem',
  paddingLeft: '0.375rem',
  paddingRight: '0.375rem',
  backgroundColor: $theme.colors.bgPulseTrack,
  color: $theme.colors.textPulseSaleMuted,
  fontSize: '0.75rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  textAlign: 'center',
  boxSizing: 'border-box',
}))

export const CollapsibleBody = styled('div', {
  minWidth: 0,
})

export const SectionChevron = styled('span', {
  display: 'inline-flex',
  marginLeft: 'auto',
})

export const ProductLabel = styled('span', {
  flexGrow: 1,
  minWidth: 0,
})

export const ProductName = styled('span', {
  flexGrow: 1,
  minWidth: 0,
})

export const EmptyText = styled('p', ({ $theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  fontSize: '0.8125rem',
  lineHeight: '1.1875rem',
  color: $theme.colors.textPulseMuted,
}))

export const FormulaSubnav = styled('div', ({ $theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  marginTop: '2px',
  marginBottom: '0.25rem',
  marginLeft: '1.125rem',
  borderLeftWidth: '2px',
  borderLeftStyle: 'solid',
  borderLeftColor: $theme.colors.borderPulseScriptLine,
  ...enter({ from: { clipPath: 'inset(0 0 100% 0)' }, to: { clipPath: 'inset(0 0 0 0)' } }, 320),
}))

export const FormulaItem = styled<'button', { $selected: boolean }>('button', ({ $theme, $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  position: 'relative',
  zIndex: 1,
  width: 'calc(100% + 2px)',
  marginLeft: '-2px',
  paddingTop: '0.375rem',
  paddingBottom: '0.375rem',
  paddingLeft: '0.875rem',
  paddingRight: '0.625rem',
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderRightWidth: 0,
  borderLeftWidth: '2px',
  borderLeftStyle: 'solid',
  borderLeftColor: 'transparent',
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: '0.5rem',
  borderBottomRightRadius: '0.5rem',
  backgroundColor: 'transparent',
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseSaleLabel,
  fontFamily: SALE_LIST_FONT,
  fontSize: '0.8125rem',
  lineHeight: '1.125rem',
  fontWeight: $selected ? 600 : 400,
  textAlign: 'left',
  cursor: 'pointer',
  ...enter(
    { from: { opacity: 0, transform: 'translateX(-6px)' }, to: { opacity: 1, transform: 'none' } },
    260,
    railStaggerDelay(80),
  ),
  ...railStagger(12, 40),
  transitionProperty: 'background-color, color, border-color',
  transitionDuration: '150ms',
  ':hover': { backgroundColor: $theme.colors.bgPulseTrack },
  ':disabled': { cursor: 'default', opacity: $selected ? 1 : 0.5 },
  ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseFocus },
}))

const formulaSlide = (animated: boolean): StyleObject => ({
  transitionProperty: animated ? 'transform, height' : 'none',
  transitionDuration: '280ms',
  transitionTimingFunction: RAIL_EASE,
  '@media (prefers-reduced-motion: reduce)': { transitionProperty: 'none' },
})

export const FormulaHighlight = styled<'span', { $box: PillBox; $animated: boolean }>(
  'span',
  ({ $theme, $box, $animated }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: `${$box.width - 2}px`,
    height: `${$box.height}px`,
    transform: `translateY(${$box.top}px)`,
    borderTopRightRadius: '0.5rem',
    borderBottomRightRadius: '0.5rem',
    backgroundColor: $theme.colors.bgPulseCanvas,
    pointerEvents: 'none',
    ...formulaSlide($animated),
  }),
)

export const FormulaIndicator = styled<'span', { $box: PillBox; $animated: boolean }>(
  'span',
  ({ $theme, $box, $animated }) => ({
    position: 'absolute',
    top: 0,
    left: '-2px',
    zIndex: 2,
    width: '2px',
    height: `${$box.height}px`,
    transform: `translateY(${$box.top}px)`,
    backgroundColor: $theme.colors.primaryPulse,
    pointerEvents: 'none',
    ...formulaSlide($animated),
  }),
)

export const FormulaPrice = styled<'span', { $selected: boolean }>('span', ({ $theme, $selected }) => ({
  flexShrink: 0,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseSaleStrong,
}))

export const SheetCard = styled(SaleCard, {
  paddingTop: '2.5rem',
  paddingBottom: '3rem',
  paddingLeft: '2.5rem',
  paddingRight: '2.5rem',
})

export const SheetContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  ...enter(riseIn, 320),
})

export const SheetNotices = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  marginBottom: '1.25rem',
  ':empty': { display: 'none' },
})

export const FormulaDescription = styled('div', ({ $theme }) => ({
  ...radius('0.5rem'),
  paddingTop: '0.75rem',
  paddingBottom: '0.75rem',
  paddingLeft: '0.875rem',
  paddingRight: '0.875rem',
  backgroundColor: $theme.colors.bgPulseCanvas,
  color: $theme.colors.textPulseStrong,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
}))

export const PoleTrigger = styled<'button', { $open: boolean; $missing: boolean }>(
  'button',
  ({ $theme, $open, $missing }) => ({
    ...radius('0.5rem'),
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    width: '100%',
    paddingTop: '0.375rem',
    paddingBottom: '0.375rem',
    paddingLeft: '0.375rem',
    paddingRight: '0.625rem',
    color: $theme.colors.textPulseMuted,
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    boxShadow: pulseLighting($theme).shadowPulseFocusIdle,
    transitionProperty: 'border-color, box-shadow, background-color',
    transitionDuration: '150ms',
    ...selectBoxState($theme, $open, false, $missing),
    ':focus-visible': { outline: 'none', ...selectBoxState($theme, true, false, false) },
  }),
)

const tileBackground = ($theme: Theme, onTint: boolean) =>
  onTint ? $theme.colors.white : $theme.colors.bgPulseSelected

export const PoleIconTile = styled<'span', { $small?: boolean; $onTint: boolean }>(
  'span',
  ({ $theme, $small, $onTint }) => ({
    ...radius($small ? '0.5rem' : '0.375rem'),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: $small ? '1.875rem' : '2rem',
    height: $small ? '1.875rem' : '2rem',
    backgroundColor: tileBackground($theme, $onTint),
    color: $theme.colors.primaryPulse,
  }),
)

export const PoleTriggerText = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
})

export const PoleTriggerLabel = styled('span', ({ $theme }) => ({
  fontSize: '0.75rem',
  lineHeight: '1rem',
  fontWeight: 500,
  color: $theme.colors.textPulseLabel,
}))

export const PoleTriggerValue = styled<'span', { $placeholder: boolean }>('span', ({ $theme }) => ({
  overflow: 'hidden',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: $theme.colors.textPulseSaleStrong,
}))

export const PoleChevron = styled<'span', { $open: boolean }>('span', ({ $open }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  transform: $open ? 'rotate(180deg)' : 'none',
  transitionProperty: 'transform',
  transitionDuration: '150ms',
}))

export const polePopoverOverrides = {
  Body: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...radius('0.75rem'),
      marginTop: '0.375rem',
      backgroundColor: $theme.colors.white,
      boxShadow: pulseLighting($theme).shadowPulseMenu,
      zIndex: 1300,
    }),
  },
  Inner: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...radius('0.75rem'),
      ...border($theme.colors.borderPulse),
      backgroundColor: $theme.colors.white,
    }),
  },
}

export const PoleMenu = styled<'div', { $width: number }>('div', ({ $theme, $width }) => ({
  fontFamily: $theme.typography.font300.fontFamily,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  width: 'max-content',
  minWidth: $width ? `${$width}px` : '16.25rem',
  maxWidth: '22.5rem',
  paddingTop: '0.375rem',
  paddingBottom: '0.375rem',
  paddingLeft: '0.375rem',
  paddingRight: '0.375rem',
  boxSizing: 'border-box',
}))

export const PoleDivider = styled('div', ({ $theme }) => ({
  height: '1px',
  marginTop: '0.25rem',
  marginBottom: '0.25rem',
  marginLeft: '0.5rem',
  marginRight: '0.5rem',
  backgroundColor: $theme.colors.borderPulseSaleCard,
}))

export const PoleOption = styled<'button', { $selected: boolean }>('button', ({ $theme, $selected }) => ({
  ...radius('0.5rem'),
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  width: '100%',
  paddingTop: '0.375rem',
  paddingBottom: '0.375rem',
  paddingLeft: '0.5rem',
  paddingRight: '0.5rem',
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  backgroundColor: $selected ? $theme.colors.bgPulseField : 'transparent',
  fontFamily: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  ':hover': { backgroundColor: $selected ? $theme.colors.bgPulseField : $theme.colors.bgPulseTrack },
  ':focus-visible': { outline: 'none', backgroundColor: $theme.colors.bgPulseTrack },
}))

export const PoleName = styled<'span', { $selected: boolean }>('span', ({ $theme, $selected }) => ({
  flexGrow: 1,
  whiteSpace: 'nowrap',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: $selected ? 600 : 500,
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseStrong,
}))

export const PoleCount = styled('span', ({ $theme }) => ({
  flexShrink: 0,
  fontSize: '0.75rem',
  lineHeight: '1rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: $theme.colors.textPulseMuted,
}))

export const PoleCheck = styled<'span', { $visible: boolean }>('span', ({ $theme, $visible }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  color: $theme.colors.primaryPulse,
  visibility: $visible ? 'visible' : 'hidden',
}))

export const SelectField = styled<'div', { $width: number }>('div', ({ $width }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  width: `${$width}px`,
  maxWidth: '100%',
}))

export const SelectLabel = styled('span', ({ $theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: '1.125rem',
  fontWeight: 500,
  color: $theme.colors.textPulseLabel,
}))

export const SelectRequired = styled('span', ({ $theme }) => ({
  color: $theme.colors.errorInput,
}))

const selectBoxState = ($theme: Theme, open: boolean, disabled: boolean, missing: boolean): StyleObject => {
  const lighting = pulseLighting($theme)
  if (disabled) return { ...border($theme.colors.borderPulse), backgroundColor: $theme.colors.bgPulseTrack }
  if (open) {
    return {
      ...border($theme.colors.primaryPulse),
      backgroundColor: $theme.colors.white,
      boxShadow: lighting.shadowPulseFocus,
    }
  }
  if (missing) return { ...border($theme.colors.missPulseBorder), backgroundColor: $theme.colors.missPulseBg }

  return {
    ...border($theme.colors.borderPulseField),
    backgroundColor: $theme.colors.bgPulseField,
    ':hover': border($theme.colors.borderPulseFieldHover),
  }
}

export const SelectBox = styled<'div', { $open: boolean; $disabled: boolean; $missing: boolean; $field?: boolean }>(
  'div',
  ({ $theme, $open, $disabled, $missing, $field = false }) => ({
    ...radius('0.5rem'),
    display: 'flex',
    alignItems: 'center',
    gap: $field ? '0.25rem' : '0.5rem',
    height: '2.25rem',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: $field ? '0.75rem' : '0.25rem',
    paddingRight: '0.5rem',
    boxSizing: 'border-box',
    cursor: $disabled ? 'default' : 'pointer',
    boxShadow: pulseLighting($theme).shadowPulseFocusIdle,
    transitionProperty: 'border-color, box-shadow, background-color',
    transitionDuration: '150ms',
    ...selectBoxState($theme, $open, $disabled, $missing),
    ':has(:focus-visible)': selectBoxState($theme, true, false, false),
  }),
)

export const SelectButton = styled('button', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  flexGrow: 1,
  minWidth: 0,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  textAlign: 'left',
  color: $theme.colors.textPulseSaleStrong,
  cursor: 'inherit',
  ':focus-visible': { outline: 'none' },
}))

export const SelectTile = styled<'span', { $onTint: boolean }>('span', ({ $theme, $onTint }) => ({
  ...radius('0.375rem'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '1.75rem',
  height: '1.75rem',
  backgroundColor: tileBackground($theme, $onTint),
  color: $theme.colors.primaryPulse,
}))

export const SelectValue = styled<'span', { $placeholder: boolean; $field?: boolean }>('span', ({ $theme }) => ({
  overflow: 'hidden',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 400,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: $theme.colors.textPulseSaleStrong,
}))

export const SelectChevron = styled<'span', { $open: boolean }>('span', ({ $theme, $open }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  color: $theme.colors.textPulseSaleMuted,
  transform: $open ? 'rotate(180deg)' : 'none',
  transitionProperty: 'transform',
  transitionDuration: '150ms',
}))

export const SelectOptionTile = styled<'span', { $onTint: boolean }>('span', ({ $theme, $onTint }) => ({
  ...radius('0.5rem'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '1.625rem',
  height: '1.625rem',
  backgroundColor: tileBackground($theme, $onTint),
  color: $theme.colors.primaryPulse,
}))

export const SelectOptionText = styled('span', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 500,
  color: $theme.colors.textPulseSaleStrong,
}))

export const SelectOptionSub = styled('span', ({ $theme }) => ({
  fontSize: '0.75rem',
  lineHeight: '1rem',
  fontWeight: 400,
  color: $theme.colors.textPulseSaleMuted,
}))

export const SelectEmpty = styled('div', ({ $theme }) => ({
  ...radius('0.75rem'),
  ...border($theme.colors.borderPulse),
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  paddingTop: '0.375rem',
  paddingBottom: '0.375rem',
  paddingLeft: '0.375rem',
  paddingRight: '0.75rem',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: $theme.colors.textPulseSaleMuted,
}))

export type ShellMessageKind = 'info' | 'caution' | 'danger' | 'success'

const MESSAGE_COLORS: Record<ShellMessageKind, { bg: string; icon: string }> = {
  info: { bg: 'bgPulseMsgInfo', icon: 'iconPulseMsgInfo' },
  caution: { bg: 'bgPulseMsgCaution', icon: 'iconPulseMsgCaution' },
  danger: { bg: 'bgPulseMsgDanger', icon: 'iconPulseMsgDanger' },
  success: { bg: 'bgPulseMsgSuccess', icon: 'iconPulseMsgSuccess' },
}

const themeColor = ($theme: Theme, token: string) => ($theme.colors as unknown as Record<string, string>)[token]

export const MessageBox = styled<'div', { $kind: ShellMessageKind; $fit?: boolean }>(
  'div',
  ({ $theme, $kind, $fit = false }) => ({
    ...radius('0.5rem'),
    display: 'flex',
    ...($fit ? { width: 'fit-content' } : {}),
    gap: '0.5rem',
    maxWidth: '51rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    backgroundColor: themeColor($theme, MESSAGE_COLORS[$kind].bg),
    color: $theme.colors.textPulseSaleLabel,
  }),
)

export const MessageIcon = styled<'span', { $kind: ShellMessageKind; $flush?: boolean }>(
  'span',
  ({ $theme, $kind, $flush = false }) => ({
    display: 'inline-flex',
    flexShrink: 0,
    marginTop: $flush ? 0 : '0.1875rem',
    color: themeColor($theme, MESSAGE_COLORS[$kind].icon),
  }),
)

export const MessageBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  minWidth: 0,
  fontSize: '1rem',
  lineHeight: '1.625rem',
  textAlign: 'justify',
})

export const PhoneLine = styled('p', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: 0,
  marginBottom: 0,
  fontSize: '1.125rem',
  lineHeight: '1.375rem',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: $theme.colors.primaryPulse,
}))

export const VisuallyHidden = styled('span', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
})

export const ActionRow = styled<'div', { $center?: boolean }>('div', ({ $center }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: $center ? 'center' : 'flex-start',
  gap: '1rem 1.5rem',
}))

export const ShellButton = styled<'button', { $kind: 'plain' | 'ghost' }>('button', ({ $theme, $kind }) => ({
  ...radius('0.5rem'),
  ...border($theme.colors.primaryPulse),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  paddingTop: '0.5rem',
  paddingBottom: '0.5rem',
  paddingLeft: '1rem',
  paddingRight: '1rem',
  backgroundColor: $kind === 'plain' ? $theme.colors.primaryPulse : 'transparent',
  color: $kind === 'plain' ? $theme.colors.white : $theme.colors.primaryPulse,
  fontFamily: 'inherit',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  cursor: 'pointer',
  transitionProperty: 'background-color, border-color, color',
  transitionDuration: '150ms',
  ':hover': {
    backgroundColor: $kind === 'plain' ? $theme.colors.primaryPulseHover : $theme.colors.bgPulseSelected,
  },
  ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseOpen },
  ':disabled': {
    ...border($theme.colors.bgPulseButtonDisabled),
    backgroundColor: $theme.colors.bgPulseButtonDisabled,
    color: $theme.colors.textPulseButtonDisabled,
    cursor: 'default',
  },
}))

export const SignatureCard = styled('div', ({ $theme }) => ({
  ...radius('0.625rem'),
  ...border($theme.colors.borderPulseSaleCard),
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  paddingTop: '0.75rem',
  paddingBottom: '0.75rem',
  paddingLeft: '1rem',
  paddingRight: '1rem',
  backgroundColor: $theme.colors.white,
}))

export const SignatureList = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  marginTop: 0,
  marginBottom: 0,
  paddingLeft: 0,
  listStyleType: 'none',
})

export const SignatureRow = styled('li', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.9375rem',
  lineHeight: '1.375rem',
  color: $theme.colors.textPulseSaleStrong,
}))

export const SignatureIcon = styled<'span', { $state: 'done' | 'loading' | 'failed' }>('span', ({ $theme, $state }) => {
  const colors = {
    done: $theme.colors.textPulseStageDone,
    loading: $theme.colors.primaryPulse,
    failed: $theme.colors.dangerPulse,
  }

  return { display: 'inline-flex', flexShrink: 0, color: colors[$state] }
})

export const SignatureHint = styled('p', ({ $theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  fontSize: '0.8125rem',
  lineHeight: '1.1875rem',
  color: $theme.colors.textPulseSaleMuted,
}))

export const DirectionLine = styled('p', ({ $theme }) => ({
  display: 'flex',
  gap: '0.5rem',
  marginTop: 0,
  marginBottom: 0,
  fontSize: '1rem',
  lineHeight: '1.625rem',
  textAlign: 'justify',
  color: $theme.colors.textPulseSaleLabel,
}))

export const DirectionIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  marginTop: '0.1875rem',
  color: $theme.colors.textPulseSaleMuted,
}))

export const MutedText = styled('span', ({ $theme }) => ({
  fontSize: '1rem',
  lineHeight: '1.625rem',
  color: $theme.colors.textPulseSaleLabel,
}))

export const PendingLabel = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 700,
  color: $theme.colors.iconPulseMsgCaution,
}))

export type ProgressTone = 'new' | 'progress' | 'wait' | 'signed' | 'error'
export type ProgressStepState = 'done' | 'current' | 'todo'

export const StatusSection = styled('section', ({ $theme }) => ({
  ...enter(riseIn, 320, '60ms'),
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: '1rem',
  paddingBottom: '0.875rem',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: $theme.colors.borderPulseSaleCard,
}))

export const StatusHeader = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '0.25rem 1rem',
})

export const StatusTitle = styled('h2', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: 0,
  marginBottom: 0,
  whiteSpace: 'nowrap',
  fontSize: '0.9375rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  color: $theme.colors.textPulseSaleStrong,
}))

export const StatusTitleIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.primaryPulse,
}))

const toneColor = ($theme: Theme, tone: ProgressTone) => {
  const colors: Record<ProgressTone, string> = {
    new: $theme.colors.textPulseSaleMuted,
    progress: $theme.colors.primaryPulse,
    wait: $theme.colors.iconPulseMsgCaution,
    signed: $theme.colors.textPulseStageDone,
    error: $theme.colors.dangerPulse,
  }

  return colors[tone]
}

export const StatusLabel = styled<'p', { $tone: ProgressTone }>('p', ({ $theme, $tone }) => ({
  flexGrow: 1,
  minWidth: 0,
  marginTop: 0,
  marginBottom: 0,
  fontSize: '0.8125rem',
  lineHeight: '1.1875rem',
  fontWeight: 700,
  textAlign: 'right',
  color: toneColor($theme, $tone),
}))

export const Stepper = styled<'ol', { $count: number }>('ol', ({ $count }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${$count}, minmax(0, 1fr))`,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: '-1rem',
  marginRight: '-1rem',
  paddingLeft: 0,
  listStyleType: 'none',
}))

const connector = ($theme: Theme, state: ProgressStepState): StyleObject => {
  const done = $theme.colors.textPulseStageDone
  if (state === 'done') return { backgroundColor: done }
  if (state === 'current') {
    return { backgroundImage: `repeating-linear-gradient(90deg, ${done} 0 0.25rem, transparent 0.25rem 0.5rem)` }
  }

  return { backgroundColor: $theme.colors.borderPulseSaleCard }
}

export const StepItem = styled<'li', { $state: ProgressStepState; $first: boolean }>(
  'li',
  ({ $theme, $state, $first }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    minWidth: 0,
    textAlign: 'center',
    ...railStagger(8, 90),
    '::before': $first
      ? {}
      : {
          content: '""',
          position: 'absolute',
          top: '0.5625rem',
          left: 'calc(-50% + 0.625rem)',
          right: 'calc(50% + 0.625rem)',
          height: '2px',
          ...connector($theme, $state),
          ...($state === 'todo'
            ? {}
            : {
                transformOrigin: 'left center',
                ...enter({ from: { transform: 'scaleX(0)' }, to: { transform: 'none' } }, 260, railStaggerDelay(120)),
              }),
        },
  }),
)

export const StepNode = styled<'span', { $state: ProgressStepState }>('span', ({ $theme, $state }) => {
  const done = $theme.colors.textPulseStageDone

  return {
    ...radius('624.9375rem'),
    ...border($state === 'todo' ? $theme.colors.borderPulseSaleCard : done),
    position: 'relative',
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.25rem',
    height: '1.25rem',
    boxSizing: 'border-box',
    backgroundColor: $state === 'done' ? done : $theme.colors.white,
    color: $theme.colors.white,
    ...($state === 'done' ? enter(popIn, 380, railStaggerDelay(180), RAIL_SPRING) : {}),
    ...($state === 'current'
      ? {
          animationName: {
            from: { boxShadow: `0 0 0 0 ${$theme.colors.borderPulseStageDone}` },
            to: { boxShadow: '0 0 0 8px transparent' },
          },
          animationDuration: '1.8s',
          animationTimingFunction: 'ease-out',
          animationIterationCount: 'infinite',
          '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
        }
      : {}),
  }
})

export const StepLabel = styled<'span', { $state: ProgressStepState }>('span', ({ $theme, $state }) => ({
  fontSize: '0.8125rem',
  lineHeight: '1.1875rem',
  fontWeight: $state === 'current' ? 700 : 400,
  color: $state === 'todo' ? $theme.colors.textPulseSaleMuted : $theme.colors.textPulseSaleStrong,
}))

export const StepTime = styled('span', ({ $theme }) => ({
  minHeight: '1.1875rem',
  fontSize: '0.75rem',
  lineHeight: '1.1875rem',
  fontStyle: 'italic',
  color: $theme.colors.textPulseSaleMuted,
}))

export const StatusActions = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '0.5rem',
})

export const TertiaryButton = styled('button', ({ $theme }) => ({
  ...radius('0.25rem'),
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  paddingTop: '0.25rem',
  paddingBottom: '0.25rem',
  paddingLeft: '0.5rem',
  paddingRight: '0.5rem',
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  lineHeight: '1.1875rem',
  fontWeight: 600,
  color: $theme.colors.primaryPulse,
  cursor: 'pointer',
  ':hover': { backgroundColor: $theme.colors.bgPulseSelected },
  ':disabled': { color: $theme.colors.textPulseButtonDisabled, cursor: 'default', backgroundColor: 'transparent' },
  ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseOpen },
}))

export const PanelStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minHeight: 0,
  overflowY: 'auto',
})

export const PanelBlock = styled<'div', { $last: boolean }>('div', ({ $theme, $last }) => ({
  borderBottomWidth: $last ? 0 : '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: $theme.colors.borderPulseSaleCard,
}))

export const PanelHeader = styled<'button', { $compact?: boolean; $open?: boolean }>(
  'button',
  ({ $theme, $compact, $open }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    width: '100%',
    paddingTop: $compact ? '0.5rem' : '1rem',
    paddingBottom: $compact ? '0.5rem' : '1rem',
    paddingLeft: 0,
    paddingRight: 0,
    borderTopWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    color: $compact && $open ? $theme.colors.primaryPulse : $theme.colors.textPulseSaleStrong,
    fontSize: $compact ? '0.875rem' : '0.9375rem',
    lineHeight: '1.25rem',
    fontWeight: $compact ? ($open ? 700 : 400) : 600,
    ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseOpen },
  }),
)

export const PanelHeaderTitle = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
})

export const PanelIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.primaryPulse,
}))

export const PanelChevron = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  color: $theme.colors.textPulseSaleMuted,
}))

export const PanelContent = styled<'div', { $compact?: boolean }>('div', ({ $theme, $compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  paddingBottom: $compact ? '0.75rem' : '1rem',
  fontSize: '0.8125rem',
  lineHeight: '1.1875rem',
  textAlign: 'justify',
  color: $theme.colors.textPulseSaleLabel,
}))

export const PanelText = styled('p', {
  marginTop: 0,
  marginBottom: 0,
})

export const BriefList = styled('dl', {
  display: 'flex',
  flexDirection: 'column',
  marginTop: 0,
  marginBottom: 0,
  textAlign: 'left',
})

export const BriefTerm = styled<'dt', { $first: boolean }>('dt', ({ $theme, $first }) => ({
  marginTop: $first ? 0 : '0.75rem',
  color: $theme.colors.textPulseSaleMuted,
}))

export const BriefValue = styled('dd', ({ $theme }) => ({
  marginLeft: 0,
  fontWeight: 700,
  overflowWrap: 'anywhere',
  color: $theme.colors.textPulseSaleStrong,
}))

export const BriefRules = styled('ul', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  marginTop: '0.75rem',
  marginBottom: 0,
  paddingLeft: '1rem',
  listStyleType: 'disc',
  textAlign: 'left',
  color: $theme.colors.textPulseSaleStrong,
}))

export const BriefNote = styled('p', ({ $theme }) => ({
  marginTop: '0.75rem',
  marginBottom: 0,
  color: $theme.colors.textPulseSaleMuted,
}))

export const ResourceRow = styled('button', ({ $theme }) => ({
  ...radius('0.625rem'),
  ...border($theme.colors.borderPulseSaleCard),
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  width: '100%',
  paddingTop: '0.625rem',
  paddingBottom: '0.625rem',
  paddingLeft: '0.625rem',
  paddingRight: '0.75rem',
  backgroundColor: $theme.colors.white,
  fontFamily: 'inherit',
  textAlign: 'left',
  textDecoration: 'none',
  cursor: 'pointer',
  ':hover': {
    borderTopColor: $theme.colors.primaryPulse,
    borderBottomColor: $theme.colors.primaryPulse,
    borderLeftColor: $theme.colors.primaryPulse,
    borderRightColor: $theme.colors.primaryPulse,
  },
  ':focus-visible': { outline: 'none', boxShadow: pulseLighting($theme).shadowPulseOpen },
}))

export const ResourceTile = styled('span', ({ $theme }) => ({
  ...radius('0.625rem'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '2.25rem',
  height: '2.25rem',
  backgroundColor: $theme.colors.bgPulseSelected,
  color: $theme.colors.primaryPulse,
}))

export const ResourceText = styled('span', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  gap: '2px',
  minWidth: 0,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  color: $theme.colors.textPulseSaleStrong,
}))

export const ResourceGo = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  marginTop: '2px',
  color: $theme.colors.textPulseSaleMuted,
}))

export const RetryBox = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: '0.75rem',
  paddingTop: '1.5rem',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: $theme.colors.borderPulseSaleCard,
}))

export const RetryLine = styled('p', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: 0,
  marginBottom: 0,
  fontSize: '0.8125rem',
  lineHeight: '1.1875rem',
  color: $theme.colors.textPulseSaleLabel,
}))

export const RetryHelp = styled('p', ({ $theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  fontSize: '0.8125rem',
  lineHeight: '1.125rem',
  color: $theme.colors.textPulseSaleMuted,
}))
